import React, { useState, useEffect, useRef } from 'react'
import { Input } from '../ui/input'
import { Button } from '../ui/button'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { toast } from 'sonner'
import { trackEvent, EventCategory } from '../../lib/analytics'

interface SurveyCodeInputProps {
  onCancel: () => void
}

export function SurveyCodeInput({ onCancel }: SurveyCodeInputProps) {
  const [code, setCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)

  // Auto-focus the input when component mounts
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const { data, error } = await supabase
        .from('survey_codes')
        .select('*')
        .eq('code', code.toUpperCase())
        .eq('status', 'active')
        .maybeSingle()

      if (error) throw error

      if (!data) {
        trackEvent({
          action: 'error',
          category: EventCategory.Survey,
          label: 'survey_code_invalid'
        })
        toast.error('Invalid or expired survey code. Please try again.')
        return
      }

      trackEvent({
        action: 'success',
        category: EventCategory.Survey,
        label: 'survey_code_valid'
      })
      navigate(`/survey/${code}/respond`)
    } catch (error) {
      console.error('Error validating survey code:', error)
      trackEvent({
        action: 'error',
        category: EventCategory.Survey,
        label: 'survey_code_error'
      })
      toast.error('An error occurred while validating the code. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input
        ref={inputRef}
        value={code}
        onChange={(e) => setCode(e.target.value.toUpperCase())}
        placeholder="Enter 8-digit code"
        maxLength={8}
        className="text-center uppercase"
        disabled={isLoading}
      />
      <div className="flex gap-2">
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          className="flex-1"
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          className="flex-1"
          disabled={code.length !== 8 || isLoading}
        >
          {isLoading ? 'Validating...' : 'Continue'}
        </Button>
      </div>
    </form>
  )
}